<template>
  <div class="space-y-6">
    <!-- En-tête avec actions -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">Édition du Guide</h3>
        <p class="text-sm text-gray-600">Gérez le sommaire et le contenu (texte et vidéo) des guides</p>
      </div>
      <div class="flex items-center gap-2">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Annuler
        </button>
        <button
          @click="saveAll"
          :disabled="saving"
          class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
        >
          <svg v-if="saving" class="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>{{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}</span>
        </button>
      </div>
    </div>

    <!-- Liste des modules avec édition -->
    <div class="space-y-4">
      <div
        v-for="module in editableModules"
        :key="module.key"
        class="border border-gray-200 rounded-lg overflow-hidden"
      >
        <!-- En-tête du module -->
        <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <div class="flex items-center space-x-3 flex-1">
            <button
              @click="toggleModuleExpanded(module.key)"
              class="text-gray-500 hover:text-gray-700"
            >
              <svg
                class="w-5 h-5 transition-transform"
                :class="{ 'rotate-90': expandedModules.has(module.key) }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
            <input
              v-model="module.label"
              type="text"
              class="flex-1 px-3 py-1 border border-gray-300 rounded text-sm font-semibold"
              placeholder="Label du module"
            />
            <input
              v-model.number="module.order"
              type="number"
              class="w-20 px-2 py-1 border border-gray-300 rounded text-xs"
              placeholder="Ordre"
            />
          </div>
          <div class="flex items-center space-x-2">
            <button
              @click="addSection(module.key)"
              class="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
              title="Ajouter une section"
            >
              + Section
            </button>
            <button
              @click="deleteModule(module.key)"
              class="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
              title="Supprimer le module"
            >
              Supprimer
            </button>
          </div>
        </div>

        <!-- Contenu du module (expansible) -->
        <div v-if="expandedModules.has(module.key)" class="p-4 space-y-4">
          <!-- Description du module -->
          <div>
            <label for="module-description" class="block text-xs font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="module-description"
              v-model="module.description"
              rows="2"
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              placeholder="Description du module"
            />
          </div>

          <!-- Sections du module -->
          <div class="space-y-3">
            <div
              v-for="section in module.sections"
              :key="section.id"
              class="border border-gray-200 rounded-lg p-4 bg-white"
            >
              <div class="flex items-start justify-between mb-3">
                <input
                  v-model="section.title"
                  type="text"
                  class="flex-1 px-3 py-1 border border-gray-300 rounded text-sm font-medium"
                  placeholder="Titre de la section"
                />
                <button
                  @click="deleteSection(module.key, section.id)"
                  class="ml-2 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                >
                  Supprimer
                </button>
              </div>

              <!-- Onglets pour Texte / Vidéo -->
              <div class="mb-3 border-b border-gray-200">
                <nav class="flex space-x-4">
                  <button
                    @click="setSectionMode(module.key, section.id, 'text')"
                    class="px-3 py-1 text-xs border-b-2 transition-colors"
                    :class="getSectionMode(module.key, section.id) === 'text'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'"
                  >
                    Texte
                  </button>
                  <button
                    @click="setSectionMode(module.key, section.id, 'video')"
                    class="px-3 py-1 text-xs border-b-2 transition-colors"
                    :class="getSectionMode(module.key, section.id) === 'video'
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'"
                  >
                    Vidéo
                  </button>
                </nav>
              </div>

              <!-- Contenu Texte -->
              <div v-if="getSectionMode(module.key, section.id) === 'text'" class="space-y-3">
                <div>
                  <label :for="`section-text-${section.id}`" class="block text-xs font-medium text-gray-700 mb-1">Texte</label>
                  <textarea
                    :id="`section-text-${section.id}`"
                    v-model="section.text"
                    rows="4"
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Contenu textuel de la section"
                  />
                </div>

                <!-- Étapes -->
                <div>
                  <label :for="`section-steps-${section.id}`" class="block text-xs font-medium text-gray-700 mb-1">Étapes (une par ligne)</label>
                  <textarea
                    :id="`section-steps-${section.id}`"
                    :value="section.steps?.join('\n') || ''"
                    @input="updateSteps(module.key, section.id, $event)"
                    rows="4"
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Étape 1&#10;Étape 2&#10;..."
                  />
                </div>

                <!-- Image -->
                <div>
                  <label :for="`section-image-${section.id}`" class="block text-xs font-medium text-gray-700 mb-1">Image</label>
                  <div class="flex items-center space-x-2">
                    <input
                      v-model="section.imageName"
                      type="text"
                      class="flex-1 px-3 py-2 border border-gray-300 rounded text-sm"
                      placeholder="Nom de l'image (ex: auth-login.png)"
                      readonly
                    />
                    <input
                      type="file"
                      accept="image/*"
                      @change="handleImageSelect(module.key, section.id, $event)"
                      class="hidden"
                      :ref="el => setImageInputRef(module.key, section.id, el)"
                    />
                    <button
                      @click="triggerImageUpload(module.key, section.id)"
                      class="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                    >
                      Sélectionner
                    </button>
                    <button
                      v-if="getImagePreview(module.key, section.id)"
                      @click="uploadImage(module.key, section.id)"
                      class="px-3 py-2 bg-green-100 text-green-700 rounded hover:bg-green-200 text-sm"
                      :disabled="uploadingImages[`${module.key}-${section.id}`]"
                    >
                      {{ uploadingImages[`${module.key}-${section.id}`] ? 'Upload...' : 'Uploader' }}
                    </button>
                  </div>
                  
                  <!-- Preview avant upload -->
                  <div v-if="getImagePreview(module.key, section.id)" class="mt-2">
                    <p class="text-xs text-gray-500 mb-1">Aperçu (avant upload) :</p>
                    <img
                      :src="getImagePreview(module.key, section.id)"
                      alt="Preview"
                      class="max-w-xs rounded border border-gray-300"
                    />
                    <button
                      @click="clearImagePreview(module.key, section.id)"
                      class="mt-1 text-xs text-red-600 hover:text-red-800"
                    >
                      Annuler
                    </button>
                  </div>
                  
                  <!-- Image uploadée -->
                  <div v-else-if="section.imageName" class="mt-2">
                    <p class="text-xs text-gray-500 mb-1">Image uploadée :</p>
                    <img
                      :src="getImageUrl(section.imageName)"
                      alt="Preview"
                      class="max-w-xs rounded border border-gray-300"
                      @error="() => {}"
                    />
                  </div>
                </div>
              </div>

              <!-- Contenu Vidéo -->
              <div v-else class="space-y-3">
                <div>
                  <label :for="`section-video-url-${section.id}`" class="block text-xs font-medium text-gray-700 mb-1">URL de la vidéo</label>
                  <input
                    :id="`section-video-url-${section.id}`"
                    v-model="section.videoUrl"
                    type="url"
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="https://www.youtube.com/watch?v=..."
                    @blur="extractVideoInfo(module.key, section.id)"
                  />
                </div>

                <div v-if="section.videoUrl" class="p-3 bg-gray-50 rounded">
                  <p class="text-xs text-gray-600 mb-1">
                    Plateforme: <span class="font-medium">{{ section.videoPlatform || 'Non détectée' }}</span>
                  </p>
                  <p v-if="section.videoId" class="text-xs text-gray-600">
                    ID: <span class="font-medium">{{ section.videoId }}</span>
                  </p>
                </div>

                <!-- Description optionnelle pour la vidéo -->
                <div>
                  <label :for="`section-video-desc-${section.id}`" class="block text-xs font-medium text-gray-700 mb-1">Description (optionnelle)</label>
                  <textarea
                    :id="`section-video-desc-${section.id}`"
                    v-model="section.text"
                    rows="2"
                    class="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    placeholder="Description de la vidéo"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bouton pour ajouter un nouveau module -->
      <button
        @click="addModule"
        class="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center space-x-2"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        <span>Ajouter un module</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, type ComponentPublicInstance } from 'vue'
import guidesService, { type Guide, type GuideSection, GuideStatus, SectionMediaType } from '@/services/guides.service'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth.store'

interface Props {
  version: string
  modules: any[]
}

const props = defineProps<Props>()
const emit = defineEmits(['close', 'save'])

const toast = useToast()
const auth = useAuthStore()
const saving = ref(false)
const expandedModules = ref<Set<string>>(new Set())
const sectionModes = ref<Record<string, 'text' | 'video'>>({})
const imageInputRefs = ref<Record<string, HTMLInputElement>>({})
const imagePreviews = ref<Record<string, string>>({}) // URLs de preview (ObjectURL)
const selectedFiles = ref<Record<string, File>>({}) // Fichiers sélectionnés avant upload
const uploadingImages = ref<Record<string, boolean>>({}) // État d'upload par section

// Créer une copie éditable des modules
const editableModules = reactive(
  props.modules.map(m => ({
    ...m,
    sections: m.sections.map((s: any) => ({ ...s }))
  }))
)

const toggleModuleExpanded = (key: string) => {
  if (expandedModules.value.has(key)) {
    expandedModules.value.delete(key)
  } else {
    expandedModules.value.add(key)
  }
}

const getSectionMode = (moduleKey: string, sectionId: string): 'text' | 'video' => {
  return sectionModes.value[`${moduleKey}-${sectionId}`] || 'text'
}

const setSectionMode = (moduleKey: string, sectionId: string, mode: 'text' | 'video') => {
  sectionModes.value[`${moduleKey}-${sectionId}`] = mode
}

const addModule = () => {
  const newModule = {
    key: `module-${Date.now()}`,
    label: 'Nouveau module',
    description: '',
    order: editableModules.length,
    sections: []
  }
  editableModules.push(newModule)
  expandedModules.value.add(newModule.key)
}

const deleteModule = (key: string) => {
  const index = editableModules.findIndex(m => m.key === key)
  if (index !== -1 && confirm('Êtes-vous sûr de vouloir supprimer ce module ?')) {
    editableModules.splice(index, 1)
    expandedModules.value.delete(key)
  }
}

const addSection = (moduleKey: string) => {
  const module = editableModules.find(m => m.key === moduleKey)
  if (module) {
    const newSection: GuideSection = {
      id: `section-${Date.now()}`,
      title: 'Nouvelle section',
      text: '',
      steps: []
    }
    module.sections.push(newSection)
    expandedModules.value.add(moduleKey)
  }
}

const deleteSection = (moduleKey: string, sectionId: string) => {
  const module = editableModules.find(m => m.key === moduleKey)
  if (module && confirm('Êtes-vous sûr de vouloir supprimer cette section ?')) {
    const index = module.sections.findIndex((s: any) => s.id === sectionId)
    if (index !== -1) {
      module.sections.splice(index, 1)
    }
  }
}

const updateSteps = (moduleKey: string, sectionId: string, event: Event) => {
  const module = editableModules.find(m => m.key === moduleKey)
  if (module) {
    const section = module.sections.find((s: any) => s.id === sectionId)
    if (section) {
      const textarea = event.target as HTMLTextAreaElement
      section.steps = textarea.value.split('\n').filter(s => s.trim())
    }
  }
}

const setImageInputRef = (moduleKey: string, sectionId: string, el: Element | ComponentPublicInstance | null) => {
  if (el && el instanceof HTMLInputElement) {
    imageInputRefs.value[`${moduleKey}-${sectionId}`] = el
  }
}

const triggerImageUpload = (moduleKey: string, sectionId: string) => {
  const input = imageInputRefs.value[`${moduleKey}-${sectionId}`]
  if (input) {
    input.click()
  }
}

// Gérer la sélection d'image (créer le preview)
const handleImageSelect = (moduleKey: string, sectionId: string, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return

  // Vérifier le type de fichier
  if (!file.type.match(/^image\/(jpg|jpeg|png|gif|webp|svg)$/)) {
    toast.error('Seules les images sont autorisées (jpg, jpeg, png, gif, webp, svg)')
    input.value = ''
    return
  }

  // Vérifier la taille (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    toast.error('La taille du fichier ne doit pas dépasser 5MB')
    input.value = ''
    return
  }

  const key = `${moduleKey}-${sectionId}`
  
  // Libérer l'ancienne preview si elle existe
  if (imagePreviews.value[key]) {
    URL.revokeObjectURL(imagePreviews.value[key])
  }

  // Créer une URL de preview
  imagePreviews.value[key] = URL.createObjectURL(file)
  selectedFiles.value[key] = file
}

// Obtenir l'URL de preview
const getImagePreview = (moduleKey: string, sectionId: string): string | undefined => {
  return imagePreviews.value[`${moduleKey}-${sectionId}`]
}

// Effacer le preview
const clearImagePreview = (moduleKey: string, sectionId: string) => {
  const key = `${moduleKey}-${sectionId}`
  if (imagePreviews.value[key]) {
    URL.revokeObjectURL(imagePreviews.value[key])
    delete imagePreviews.value[key]
  }
  delete selectedFiles.value[key]
  
  // Réinitialiser l'input file
  const input = imageInputRefs.value[key]
  if (input) {
    input.value = ''
  }
}

// Uploader l'image
const uploadImage = async (moduleKey: string, sectionId: string) => {
  const key = `${moduleKey}-${sectionId}`
  const file = selectedFiles.value[key]
  
  if (!file) {
    toast.error('Aucun fichier sélectionné')
    return
  }

  const module = editableModules.find(m => m.key === moduleKey)
  if (!module) {
    toast.error('Module non trouvé')
    return
  }

  uploadingImages.value[key] = true

  try {
    // Vérifier si le guide existe déjà, sinon le créer
    let guideId = module._id
    if (!guideId) {
      // Créer le guide d'abord
      const guideData: Partial<Guide> = {
        key: module.key,
        label: module.label,
        description: module.description,
        version: props.version,
        order: module.order ?? 0,
        sections: module.sections.map((s: any) => ({
          id: s.id,
          title: s.title,
          text: s.text,
          imageName: s.imageName,
          steps: s.steps,
          links: s.links || [],
          actions: s.actions || [],
          videoUrl: s.videoUrl,
          videoPlatform: s.videoPlatform,
          videoId: s.videoId
        })),
        status: GuideStatus.PUBLISHED,
        tags: module.tags || [],
        metadata: module.metadata || {}
      }
      
      const createdGuide = await guidesService.create(guideData)
      guideId = createdGuide._id
      module._id = guideId
    }

    // Uploader l'image et mettre à jour directement le guide
    const result = await guidesService.uploadImageForSection(
      guideId,
      sectionId,
      props.version,
      file
    )

    // Mettre à jour immédiatement la section dans editableModules avec le nouveau imageName
    const updatedModule = editableModules.find(m => m.key === moduleKey)
    if (updatedModule) {
      const section = updatedModule.sections.find((s: any) => s.id === sectionId)
      if (section) {
        // Mettre à jour l'imageName et le mediaType
        section.imageName = result.filename
        // Recalculer le mediaType (image > vidéo > rien)
        if (section.videoUrl || section.videoId) {
          section.mediaType = SectionMediaType.VIDEO
        } else if (section.imageName) {
          section.mediaType = SectionMediaType.IMAGE
        } else {
          section.mediaType = SectionMediaType.NONE
        }
      }
    }

    // Recharger le guide depuis l'API pour obtenir la mise à jour complète (optionnel, pour synchronisation)
    try {
      const updatedGuide = await guidesService.findOne(guideId)
      if (updatedGuide && updatedModule) {
        // Synchroniser toutes les sections avec les données du guide mis à jour
        updatedModule.sections = updatedGuide.sections || []
      }
    } catch (error) {
      console.warn('Impossible de recharger le guide depuis l\'API:', error)
      // On continue quand même car on a déjà mis à jour localement
    }

    // Nettoyer le preview après upload réussi
    clearImagePreview(moduleKey, sectionId)

    toast.success('Image uploadée et guide mis à jour avec succès')
  } catch (error) {
    console.error('Erreur lors de l\'upload:', error)
    toast.error('Erreur lors de l\'upload de l\'image')
  } finally {
    uploadingImages.value[key] = false
  }
}

const extractVideoInfo = (moduleKey: string, sectionId: string) => {
  const module = editableModules.find(m => m.key === moduleKey)
  if (!module) return

  const section = module.sections.find((s: any) => s.id === sectionId)
  if (!section || !section.videoUrl) return

  // Extraire l'ID et la plateforme depuis l'URL
  const url = section.videoUrl

  // YouTube - Pattern simplifié
  const youtubePattern = /(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const youtubeMatch = youtubePattern.exec(url)
  if (youtubeMatch?.[1]) {
    section.videoId = youtubeMatch[1]
    section.videoPlatform = 'youtube'
    return
  }

  // Vimeo
  const vimeoPattern = /vimeo\.com\/(\d+)/
  const vimeoMatch = vimeoPattern.exec(url)
  if (vimeoMatch?.[1]) {
    section.videoId = vimeoMatch[1]
    section.videoPlatform = 'vimeo'
    return
  }

  // Dailymotion
  const dailymotionPattern = /dailymotion\.com\/(?:video|embed)\/([a-zA-Z0-9]+)/
  const dailymotionMatch = dailymotionPattern.exec(url)
  if (dailymotionMatch?.[1]) {
    section.videoId = dailymotionMatch[1]
    section.videoPlatform = 'dailymotion'
    return
  }

  section.videoPlatform = 'other'
}

const getImageUrl = (filename: string): string => {
  return guidesService.getImageUrl(props.version, filename)
}

const saveAll = async () => {
  saving.value = true
  try {
    // Sauvegarder chaque module comme un guide
    for (const module of editableModules) {
      // Récupérer l'organizationId de l'utilisateur si externe
      let userOrgId: string | undefined
      if (auth.user?.organizationId) {
        if (typeof auth.user.organizationId === 'object') {
          userOrgId = auth.user.organizationId._id?.toString() || auth.user.organizationId.toString()
        } else {
          userOrgId = auth.user.organizationId.toString()
        }
      }

      const guideData: Partial<Guide> = {
        key: module.key,
        label: module.label,
        description: module.description,
        version: props.version,
        order: module.order ?? 0,
        sections: (module.sections || []).map((s: any) => ({
          id: s.id,
          title: s.title,
          text: s.text,
          imageName: s.imageName,
          steps: s.steps,
          links: s.links || [],
          actions: s.actions || [],
          videoUrl: s.videoUrl,
          videoPlatform: s.videoPlatform,
          videoId: s.videoId
        })),
        status: GuideStatus.PUBLISHED,
        organizationId: module.organizationId || userOrgId, // Utiliser celui du module ou de l'utilisateur
        tags: module.tags || [],
        metadata: module.metadata || {}
      }

      // Vérifier si le guide existe déjà
      try {
        const existing = await guidesService.findOne(module._id || '')
        if (existing) {
          await guidesService.update(module._id, guideData)
        }
      } catch {
        // Guide n'existe pas, le créer
        await guidesService.create(guideData)
      }
    }

    toast.success('Guide sauvegardé avec succès')
    emit('save')
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error)
    toast.error('Erreur lors de la sauvegarde du guide')
  } finally {
    saving.value = false
  }
}
</script>

